import React, { useEffect, useRef, useState } from 'react';
import DocViewer, { DocViewerRenderers, IDocument } from "@cyntler/react-doc-viewer";
import { renderAsync } from "docx-preview";
import * as CFB from 'cfb';
import "@cyntler/react-doc-viewer/dist/index.css";
import { FormSectionElements } from '../../entities/formTemplateDataType';
import { fileTypeList } from '../../entities/Constants';
import {ReactComponent as CloseIcon} from '../../../public/assets/css/ontario-design-system/icons/ontario-icon-close.svg';

type FilePreviewProps = {
    field: FormSectionElements;
    file: File;
    lang: 'en' | 'fr';
};

export const PdfPreviewComponent = ({field, file, lang}: FilePreviewProps) => {
    const [url, setUrl] = useState<string>('');

    useEffect(() => {
        const blobUrl = URL.createObjectURL(file);
        setUrl(blobUrl);
        return () => URL.revokeObjectURL(blobUrl);
    }, [file]);

    return (
        <>
            {url && (
                <iframe
                    src={url}
                    title={field?.Label || 'PDF Preview'}
                    style={{ width: '100%', height: '100%', border: '1px solid #ddd', flex: 1, minHeight: 0 }}
                />
            )}
        </>
    );
};

export const DocViewerComponent = ({file}: {file: File}) => {
    const getFileType = (file?: File) => {
        const types = fileTypeList.filter(type => type.val.some(val => val.mimeType === file.type))?.[0];
        if (types) {
            const type = types.val.map(val => val.mimeType === file.type ? val.ext : null).filter(Boolean)?.[0] || '';
            return type
        }
        return '';
    }

    const [docs, setDocs] = useState<IDocument[]>([{
        uri: window.URL.createObjectURL(file),
        fileName: file.name,
        fileType: getFileType(file),
    }]);

    // useEffect(() => {
    //     if (!file) return;
    //     file.arrayBuffer().then(content =>
    //         setDocs([{ uri: window.URL.createObjectURL(file), fileData: content, fileType: getFileType(file)}])
    //     ).catch(err => {
    //         console.error('load file content error', err);
    //         setDocs([]);
    //     });
    // }, [file]);

    return (
        <>
            {docs.length && (
                <DocViewer documents={docs} pluginRenderers={DocViewerRenderers} />
            )}
        </>
    )
};

export const PDFViewerComponent = ({
  pdfContent,
  filename,
  onClose,
}: {
  pdfContent: string;
  filename: string;
  onClose?: () => void;
}) => {
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState(false);

  useEffect(() => {
    try {
      const base64 = pdfContent.includes(",")
        ? pdfContent.split(",")[1]
        : pdfContent;

      const binary = atob(base64);
      const len = binary.length;
      const buffer = new Uint8Array(len);
      for (let i = 0; i < len; i++) buffer[i] = binary.charCodeAt(i);

      const blob = new Blob([buffer], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      setUrl(blobUrl);

      return () => URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Invalid PDF Base64:", err);
      setError(true);
      return undefined;
    }
  }, [pdfContent]);

  if (error) return <div>Failed to load PDF</div>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>{filename}</div>
        {onClose && <button onClick={onClose}>Close</button>}
      </div>

      <div style={{ flex: 1, minHeight: 0, border: "1px solid #ddd" }}>
        {url && (
          <iframe
            src={url}
            title={filename || 'PDF Viewer'}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </div>
    </>
  );
};

export const ImageViewerComponent = ({imageData, filename, imageType, onClose}: {imageData: string; filename: string; imageType: string; onClose?: () => void}) => {
    const data = imageData.split(';base64,');
    const mimeType = data[0].split(':')[1];
    const base64 = data[1];
    const html = `
    <html>
        <body style="margin:0;padding:0;overflow:hidden;">
        <img src="data:${mimeType};base64,${base64}" style="width:100%;height:100%;object-fit:contain;display:block;" />
        </body>
    </html>
    `;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    return (
        <>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px'}}>
                <div className='ontrario-margin-top-24-! ontario-margin-bottom-24-! file-preview-title'>{filename}</div>
                {onClose && (
                    <button 
                        aria-label="Close preview" 
                        title="Close" 
                        style={{
                            cursor: 'pointer', 
                            background: 'none', 
                            border: 'none', 
                            padding: '8px', 
                            margin: '-2px 0 0 0',
                            display: 'flex',
                            alignItems: 'center'
                        }} 
                        onClick={onClose}
                    >
                        <CloseIcon style={{fill:"#0066cc", height: '24', width:'24'}} />
                    </button>
                )}
            </div>
            <div style={{display: 'flex', justifyContent: 'center', width: '100%', height: '90%'}}>
                <iframe src={url} width='100%' height='100%' style={{border: 'none'}}/>
            </div>
        </>
    )
};

/**
 * Extracts text from a legacy .doc (Word 97-2003) binary file using the OLE compound document structure.
 * Parses the FIB, piece table, and assembles text from ANSI/Unicode pieces.
 */
const extractTextFromLegacyDoc = (arrayBuffer: ArrayBuffer): string => {
    const uint8 = new Uint8Array(arrayBuffer);
    const cfbFile = CFB.read(uint8, { type: 'array' });

    const wordDocEntry = cfbFile.FileIndex.find(e => e.name === 'WordDocument');
    if (!wordDocEntry?.content) throw new Error('WordDocument stream not found');

    const wd = new Uint8Array(wordDocEntry.content as any);
    const wdView = new DataView(wd.buffer, wd.byteOffset, wd.byteLength);

    const wIdent = wdView.getUint16(0, true);
    if (wIdent !== 0xA5EC) throw new Error('Invalid Word document signature');

    const flags = wdView.getUint16(0x000A, true);
    const fWhichTblStm = (flags >> 9) & 1;
    const tableName = fWhichTblStm ? '1Table' : '0Table';

    const tableEntry = cfbFile.FileIndex.find(e => e.name === tableName);
    if (!tableEntry?.content) throw new Error(`${tableName} stream not found`);

    const td = new Uint8Array(tableEntry.content as any);
    const tdView = new DataView(td.buffer, td.byteOffset, td.byteLength);

    const ccpText = wdView.getInt32(0x004C, true);
    const fcClx = wdView.getInt32(0x01A2, true);
    const lcbClx = wdView.getInt32(0x01A6, true);

    if (fcClx <= 0 || lcbClx <= 0 || fcClx >= td.byteLength)
        throw new Error('CLX structure not found');

    // Skip optional Prc entries (type 0x01) to reach PlcPcd (type 0x02)
    let pos = fcClx;
    while (pos < fcClx + lcbClx && td[pos] === 0x01) {
        const cbGrpprl = tdView.getInt16(pos + 1, true);
        pos += 3 + cbGrpprl;
    }
    if (td[pos] !== 0x02) throw new Error('PlcPcd marker not found');

    const cbPlcPcd = tdView.getInt32(pos + 1, true);
    pos += 5;
    const plcStart = pos;

    // PlcPcd: (n+1) CPs (4 bytes each) followed by n PCDs (8 bytes each)
    // (n+1)*4 + n*8 = cbPlcPcd → n = (cbPlcPcd - 4) / 12
    const numPieces = Math.floor((cbPlcPcd - 4) / 12);
    if (numPieces <= 0) throw new Error('No text pieces found');

    let text = '';
    let totalChars = 0;

    for (let i = 0; i < numPieces; i++) {
        const cpStart = tdView.getInt32(plcStart + i * 4, true);
        const cpEnd = tdView.getInt32(plcStart + (i + 1) * 4, true);
        const charCount = cpEnd - cpStart;
        if (charCount <= 0) continue;

        const pcdOffset = plcStart + (numPieces + 1) * 4 + i * 8;
        const fcValue = tdView.getInt32(pcdOffset + 2, true);

        // Bit 30 set = ANSI (compressed), bit 30 clear = Unicode (UTF-16LE)
        const isAnsi = (fcValue & 0x40000000) !== 0;
        const fcReal = isAnsi ? (fcValue & ~0x40000000) >> 1 : fcValue;

        for (let j = 0; j < charCount; j++) {
            let ch: number;
            if (isAnsi) {
                const bytePos = fcReal + j;
                if (bytePos >= wd.byteLength) break;
                ch = wd[bytePos];
            } else {
                const bytePos = fcReal + j * 2;
                if (bytePos + 1 >= wd.byteLength) break;
                ch = wdView.getUint16(bytePos, true);
            }

            if (ch === 0x0D || ch === 0x0B) {
                text += '\n';
            } else if (ch === 0x0C) {
                text += '\n\n';
            } else if (ch >= 0x20 || ch === 0x09) {
                text += String.fromCharCode(ch);
            }
        }

        totalChars += charCount;
        if (totalChars >= ccpText) break;
    }

    return text.trim();
};



export const MsDocViewer = ({docData, fileName, onClose}: {docData: string; fileName: string; onClose?: () => void}) => {
    
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const hasRendered = useRef(false);
    const blobUrlRef = useRef<string>('');

    const isLegacyDoc = /\.doc$/i.test(fileName);

    const decodeBase64ToBuffer = (doc: string): ArrayBuffer => {
        const data = doc.split(';base64,');
        const bytes = atob(data[1]);
        const buffer = Uint8Array.from(bytes, c => c.charCodeAt(0));
        return buffer.buffer;
    };

    const revokeBlobUrl = () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = '';
        }
    };

    const loadIframeHtml = (html: string): Promise<HTMLIFrameElement> => {
        return new Promise((resolve, reject) => {
            const iframe = iframeRef.current;
            if (!iframe) { reject(new Error('iframe not mounted')); return; }
            revokeBlobUrl();
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            blobUrlRef.current = url;
            iframe.onload = () => resolve(iframe);
            iframe.onerror = () => reject(new Error('iframe load failed'));
            iframe.src = url;
        });
    };

    const renderDocxInIframe = async (arrayBuffer: ArrayBuffer) => {
        const baseHtml = `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  html, body { margin: 0; padding: 0; background: #e8e8e8; height: 100%; overflow-x: hidden; }
  #docx-container { padding: 16px; display: flex; flex-direction: column; align-items: stretch; }
  #docx-container > .docx-wrapper { max-width: 100% !important; overflow-x: auto !important; }
  #docx-container section.docx { 
    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important; 
    margin-bottom: 16px !important;
    max-width: 100% !important;
    width: 100% !important;
    overflow-x: auto !important;
    box-sizing: border-box !important;
  }
  #docx-container section.docx img { max-width: 100% !important; height: auto !important; }
  #docx-container section.docx table { max-width: 100% !important; }
  @media (max-width: 640px) {
    #docx-container { padding: 0; }
    #docx-container section.docx { 
      padding: 12px !important;
      min-width: 0 !important;
      box-shadow: none !important;
      border: none !important;
    }
    #docx-container section.docx > * { max-width: 100% !important; overflow-wrap: break-word !important; word-break: break-word !important; }
  }
</style>
</head><body><div id="docx-container"></div></body></html>`;

        const iframe = await loadIframeHtml(baseHtml);
        const iframeDoc = iframe.contentDocument;
        if (!iframeDoc) return;

        const container = iframeDoc.getElementById('docx-container');
        if (!container) return;

        await renderAsync(arrayBuffer, container, undefined, {
            inWrapper: true,
            ignoreWidth: true,
            ignoreHeight: true,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: true,
            trimXmlDeclaration: true,
            useBase64URL: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
        });
    };

    const renderLegacyDocInIframe = async (text: string) => {
        const escapedText = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        await loadIframeHtml(`<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  html, body { margin: 0; padding: 0; background: #e8e8e8; height: 100%; }
  .page {
    max-width: 816px; width: 100%; margin: 16px auto;
    background: #fff; padding: 60px 72px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12); border: 1px solid #ddd;
    font-family: 'Consolas', 'Courier New', monospace;
    font-size: 10pt; line-height: 1.4; color: #1a1a1a;
    white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word;
    box-sizing: border-box;
  }
  @media (max-width: 640px) {
    .page { padding: 24px 16px; margin: 4px auto; }
  }
</style>
</head><body><div class="page">${escapedText}</div></body></html>`);
    };

    useEffect(() => {
        if (!docData || hasRendered.current || !iframeRef.current) {
            return () => revokeBlobUrl();
        }
        hasRendered.current = true;

        const render = async () => {
            setLoading(true);
            const arrayBuffer = decodeBase64ToBuffer(docData);

            if (isLegacyDoc) {
                try {
                    const extractedText = extractTextFromLegacyDoc(arrayBuffer);
                    if (extractedText.length > 0) {
                        await renderLegacyDocInIframe(extractedText);
                        setLoading(false);
                        return;
                    }
                } catch (err) {
                    console.warn(`Legacy .doc parser failed for "${fileName}":`, err);
                }
                setError(`Unable to preview "${fileName}". Legacy .doc files with complex formatting may not be supported.`);
                setLoading(false);
                return;
            }

            // .docx: render via docx-preview inside isolated iframe
            try {
                await renderDocxInIframe(arrayBuffer);
                setLoading(false);
                return;
            } catch {
                console.warn(`docx-preview failed for "${fileName}", trying legacy parser...`);
            }

            // Fallback: try CFB in case it's a .doc renamed as .docx
            try {
                const extractedText = extractTextFromLegacyDoc(decodeBase64ToBuffer(docData));
                if (extractedText.length > 0) {
                    await renderLegacyDocInIframe(extractedText);
                    setLoading(false);
                    return;
                }
            } catch (err) {
                console.warn(`Legacy fallback also failed for "${fileName}":`, err);
            }

            setError(`Unable to preview "${fileName}". The file may be corrupted or in an unsupported format.`);
            setLoading(false);
        };

        render();
        return () => revokeBlobUrl();
    }, [docData]);

    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden'}}>
            <div style={{
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '12px 16px',
                borderBottom: '1px solid #ddd',
                flexShrink: 0,
            }}>
                <div 
                    className='ontrario-margin-top-24-! ontario-margin-bottom-24-! file-preview-title'
                    style={{wordBreak: 'break-word', minWidth: 0, flex: 1}}
                >{fileName}</div>
                {onClose && (
                    <button 
                        aria-label="Close preview" 
                        title="Close" 
                        style={{
                            cursor: 'pointer', 
                            background: 'none', 
                            border: 'none', 
                            padding: '8px', 
                            marginLeft: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                        }} 
                        onClick={onClose}
                    >
                        <CloseIcon style={{fill:"#0066cc", height: '24', width:'24'}} />
                    </button>
                )}
            </div>

            {error ? (
                <div style={{padding: '24px 16px', textAlign: 'center'}}>
                    <p style={{color: '#d8292f', marginBottom: '8px'}}>{error}</p>
                </div>
            ) : (
                <>
                    {loading && (
                        <div style={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            zIndex: 1,
                        }}>
                            <span>Loading document preview...</span>
                        </div>
                    )}
                    <iframe
                        ref={iframeRef}
                        title={`Preview: ${fileName}`}
                        style={{
                            flex: 1,
                            width: '100%',
                            minHeight: 0,
                            border: 'none',
                            background: '#e8e8e8',
                            visibility: loading ? 'hidden' : 'visible',
                        }}
                        sandbox="allow-same-origin"
                    />
                </>
            )}
        </div>
    )
}